import { Button, message, Space, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import React from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { FixedSizeList, VariableSizeList } from "react-window";

import {
  getTaskAllocationKey,
  WeeklyAllocations,
  withWeekTasks
} from "@/components/forecasting/taskAllocations";
import { features } from "@/config/features";
import { ICarryForwardTask } from "@/features/forecast-carry-forward";
import { useElementHeight } from "@/hooks/useElementHeight";
import { IBulkStageSelection, ITaskAllocation } from "@/pages/ForecastingPage";
import {
  IForecastingCentralRepoRes,
  IForecastingRes
} from "@/types/forecasting";

const { Text } = Typography;

/**
 * A plot+stage selection can put 100k+ tasks in front of the user, so both
 * lists on this step are windowed — only the rows in view are mounted. The
 * pixel constants below exist so `react-window` can position those rows.
 */
const UNALLOCATED_LIST_HEIGHT = 300;
/** Height of a single-line group row; the real height is measured after paint. */
const ESTIMATED_GROUP_HEIGHT = 46;
const ALLOCATED_CARD_HEIGHT = 62;
const GROUP_COLUMNS = "100px 200px minmax(0, 1fr)";

/** Stable identity for a week that has nothing allocated to it. */
const NO_ALLOCATED_TASKS: ITaskAllocation[] = [];

/** A task that the plot+stage selection makes available for allocation. */
interface IAvailableTask {
  allocationKey: string;
  taskId: string;
  taskName: string;
  stageId: string;
  stageName: string;
  plotNumber: string;
  stageColor?: string;
}

/** One row of the unallocated panel: a plot+stage and its remaining tasks. */
interface IUnallocatedGroup {
  key: string;
  plotNumber: string;
  stageId: string;
  stageName: string;
  stageColor?: string;
  tasks: IAvailableTask[];
}

/** A task being dragged, either on its own or as part of a multi-select. */
interface IDraggedTask {
  allocationKey: string;
  taskId: string;
  taskName: string;
  stageId: string;
  stageName: string;
  plotNumber: string;
  monthWeek?: string;
  status?: ITaskAllocation["status"];
}

interface IDragItem extends IDraggedTask {
  isMultiple?: boolean;
  multipleTasksData?: IDraggedTask[];
}

const toDraggedTask = ({
  allocationKey,
  taskId,
  taskName,
  stageId,
  stageName,
  plotNumber
}: IAvailableTask): IDraggedTask => ({
  allocationKey,
  taskId,
  taskName,
  stageId,
  stageName,
  plotNumber
});

export const AllocateTasksStep = ({
  projectCentralRepoData,
  monthForecastData: _monthForecastData,
  selectedMonth,
  bulkStageSelections,
  weeklyAllocationsRef,
  carryForwardTasks = []
}: {
  projectCentralRepoData: IForecastingCentralRepoRes;
  monthForecastData?: IForecastingRes;
  selectedMonth?: string; // Format: "YYYY-MM"
  bulkStageSelections: IBulkStageSelection[];
  weeklyAllocationsRef: React.MutableRefObject<WeeklyAllocations>;
  carryForwardTasks?: ICarryForwardTask[];
}) => {
  // Dragging a task only ever repaints this step, so the reactive copy of the
  // allocations is owned here. It is keyed by week — the way the week columns
  // are — so a drop replaces only the arrays of the weeks it touched and every
  // other column stays memoized.
  const [allocationsByWeek, setAllocationsByWeek] =
    React.useState<WeeklyAllocations>(() => weeklyAllocationsRef.current);
  // Multi-select is transient — nothing outside this step needs it.
  const [selectedTasks, setSelectedTasks] = React.useState<Set<string>>(
    new Set()
  );

  // Hand the wizard the current allocations without re-rendering it. Swapping
  // the reference is O(1), and the state object is never mutated after it is
  // built, so the ref cannot go stale behind us.
  React.useEffect(() => {
    weeklyAllocationsRef.current = allocationsByWeek;
  }, [allocationsByWeek, weeklyAllocationsRef]);

  // Build a set of carry-forward task keys for quick lookup
  const carryForwardTaskKeys = React.useMemo(() => {
    const keys = new Set<string>();
    carryForwardTasks.forEach((t) => {
      keys.add(
        getTaskAllocationKey({
          taskId: t.taskId,
          stageId: t.stage,
          plotNumber: t.plot
        })
      );
    });
    return keys;
  }, [carryForwardTasks]);

  const tasksFromSelectedStages = React.useMemo(
    () =>
      bulkStageSelections.map((selection) => {
        // selection.stageId is actually the stage name
        const stageName = selection.stageId;
        const plotNumbers = selection.plotNumbers;

        // Get tasks for each plot from projectCentralRepoData, filtering out completed tasks
        const tasksByPlot = plotNumbers.map((plotNumber) => {
          const plot = projectCentralRepoData.groundworks.plots.find(
            (p) => p.plotNo === plotNumber
          );
          const stageData = plot?.stages?.find(
            (s) => s.stageName === stageName
          );
          const tasks =
            stageData?.tasks
              ?.filter((task) => task.currentStatus !== "Completed")
              ?.map((task) => ({
                taskId: task.taskId,
                taskName: task.taskName
              })) ?? [];

          return {
            plotNumber,
            tasks
          };
        });

        return {
          stageId: stageName,
          stageName: stageName,
          stageColor: getStageColor(stageName),
          plotNumbers,
          tasksByPlot
        };
      }),
    [bulkStageSelections, projectCentralRepoData]
  );

  // Parse the selected month (format: "YYYY-MM") or fallback to current month
  const parsedDate = selectedMonth ? dayjs(selectedMonth, "YYYY-MM") : dayjs();
  const year = parsedDate.year();
  const month = parsedDate.month() + 1; // dayjs months are 0-indexed
  const numberOfWeeks = Math.ceil(parsedDate.daysInMonth() / 7);

  const monthWeeks: { month: number; year: number; week: number }[] = [];
  for (let week = 1; week <= numberOfWeeks; week++) {
    monthWeeks.push({ month, year, week });
  }

  const allAvailableTasks = React.useMemo<IAvailableTask[]>(
    () =>
      tasksFromSelectedStages.flatMap((stage) =>
        stage.tasksByPlot.flatMap(({ plotNumber, tasks }) =>
          tasks.map((task) => ({
            ...task,
            allocationKey: getTaskAllocationKey({
              taskId: task.taskId,
              stageId: stage.stageId,
              plotNumber
            }),
            plotNumber,
            stageId: stage.stageId,
            stageName: stage.stageName,
            stageColor: stage.stageColor
          }))
        )
      ),
    [tasksFromSelectedStages]
  );

  // The week columns each get their own array; the unallocated panel and the
  // "Allocated" count instead need to know which tasks are spoken for, whatever
  // week they landed in.
  const allocatedTaskKeys = React.useMemo(() => {
    const keys = new Set<string>();
    allocationsByWeek.forEach((weekTasks) => {
      weekTasks.forEach((task) => keys.add(task.allocationKey));
    });
    return keys;
  }, [allocationsByWeek]);

  const unallocatedTasks = React.useMemo(
    () =>
      allAvailableTasks.filter(
        (task) => !allocatedTaskKeys.has(task.allocationKey)
      ),
    [allAvailableTasks, allocatedTaskKeys]
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <div>
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <div style={{ display: "flex", gap: 24, fontSize: "13px" }}>
            <Space>
              <Text strong>{allAvailableTasks.length}</Text>
              <Text type="secondary">Total Tasks</Text>
            </Space>
            <Space>
              <Text strong style={{ color: "#52C41A" }}>
                {allocatedTaskKeys.size}
              </Text>
              <Text type="secondary">Allocated</Text>
            </Space>
            <Space>
              <Text strong style={{ color: "#FA8C16" }}>
                {unallocatedTasks.length}
              </Text>
              <Text type="secondary">Unallocated</Text>
            </Space>
          </div>
        </div>

        {unallocatedTasks.length > 0 && (
          <div
            style={{
              background: "#fafafa",
              border: "1px dashed #d9d9d9",
              borderRadius: "4px",
              padding: "12px",
              marginBottom: "16px"
            }}
          >
            <div
              style={{
                marginBottom: 12,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start"
              }}
            >
              <Space direction="vertical" size={4}>
                <Space>
                  <Text strong style={{ fontSize: 13 }}>
                    Unallocated Tasks
                  </Text>
                  <Tag color="orange" style={{ fontSize: 11 }}>
                    {unallocatedTasks.length}
                  </Tag>
                </Space>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, lineHeight: 0.5 }}
                >
                  Drag tasks to any week below. Use Ctrl+Click or Cmd+Click to
                  select multiple tasks.
                  <br />
                  Tasks featuring an amber dot have been carried forward to the
                  current period.
                </Text>
              </Space>
              {features.qaSelectAll && (
                <Space size={8}>
                  {selectedTasks.size > 0 && (
                    <Button
                      size="small"
                      onClick={() => setSelectedTasks(new Set())}
                    >
                      Clear ({selectedTasks.size})
                    </Button>
                  )}
                  <Button
                    size="small"
                    type="primary"
                    ghost
                    disabled={unallocatedTasks.length === 0}
                    onClick={() => {
                      setSelectedTasks(
                        new Set(unallocatedTasks.map((t) => t.allocationKey))
                      );
                    }}
                  >
                    Select All ({unallocatedTasks.length})
                  </Button>
                </Space>
              )}
            </div>

            <UnallocatedPanel
              carryForwardTaskKeys={carryForwardTaskKeys}
              unallocatedTasks={unallocatedTasks}
              selectedTasks={selectedTasks}
              setSelectedTasks={setSelectedTasks}
            />
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${numberOfWeeks}, minmax(0, 1fr))`,
            gap: "16px",
            width: "100%"
          }}
        >
          {monthWeeks.map(({ month, year, week }) => {
            const monthWeek = `${year}-${String(month).padStart(2, "0")}-W${week}`;
            return (
              <div key={monthWeek} style={{ minWidth: 0 }}>
                <WeekDropZone
                  monthWeek={monthWeek}
                  weekNumber={week}
                  allocatedTasks={
                    allocationsByWeek.get(monthWeek) ?? NO_ALLOCATED_TASKS
                  }
                  setAllocationsByWeek={setAllocationsByWeek}
                  setSelectedTasks={setSelectedTasks}
                />
              </div>
            );
          })}
        </div>
      </div>
    </DndProvider>
  );
};

// Stage colors for visual differentiation
const stageColors: Record<string, string> = {
  "Cut/Fill": "#1890ff",
  "Foundation - Exc & Conc": "#1890ff",
  "Foundation - Walls to DPC": "#1890ff",
  "Foundation Floor": "#1890ff",
  "Private Drives to Binder": "#1890ff",
  "Private Drives to Surface Course": "#1890ff",
  "Shared Drives to Surface Course": "#1890ff",
  "Plot Services": "#1890ff",
  "Service Connections": "#1890ff",
  "Plot Paving": "#1890ff",
  "Scaffold Platform": "#1890ff",
  "Topsoil/Plot Clearance": "#1890ff",
  "Screen Walls": "#1890ff"
};

const getStageColor = (stageName: string) => {
  return stageColors[stageName] || "#1890ff";
};

const DraggableTask = React.memo(
  ({
    task,
    isCarryForward,
    allTasks,
    selectedTasks,
    setSelectedTasks
  }: {
    task: IAvailableTask;
    isCarryForward?: boolean;
    allTasks: IAvailableTask[];
    selectedTasks: Set<string>;
    setSelectedTasks: React.Dispatch<React.SetStateAction<Set<string>>>;
  }) => {
    const isSelected = selectedTasks.has(task.allocationKey);

    const [{ isDragging }, drag] = useDrag({
      type: "TASK",
      item: (): IDragItem => {
        if (isSelected && selectedTasks.size > 1) {
          return {
            ...toDraggedTask(task),
            multipleTasksData: allTasks
              .filter((t) => selectedTasks.has(t.allocationKey))
              .map(toDraggedTask),
            isMultiple: true
          };
        }
        return toDraggedTask(task);
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging()
      })
    });

    const handleClick = (e: React.MouseEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();

        setSelectedTasks((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(task.allocationKey)) {
            newSet.delete(task.allocationKey);
          } else {
            newSet.add(task.allocationKey);
          }
          return newSet;
        });
      }
    };

    return (
      <div
        ref={drag}
        onClick={handleClick}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "4px 8px",
          background: isDragging
            ? "#e6f7ff"
            : isSelected
              ? "#bae7ff"
              : "#ffffff",
          border: isSelected ? "2px solid #1890ff" : "1px solid #d9d9d9",
          borderLeft: task.stageColor
            ? `3px solid ${task.stageColor}`
            : isSelected
              ? "2px solid #1890ff"
              : "1px solid #d9d9d9",
          borderRadius: "4px",
          cursor: "move",
          opacity: isDragging ? 0.5 : 1,
          fontSize: "12px",
          transition: "all 0.2s",
          position: "relative"
        }}
      >
        {isCarryForward && (
          <Tooltip title="Carried forward from previous month" placement="top">
            <span
              style={{
                position: "absolute",
                top: -3,
                right: -3,
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#FA8C16",
                border: "1px solid #fff"
              }}
            />
          </Tooltip>
        )}
        <Tooltip title={`${task.taskName} (${task.stageName})`} placement="top">
          <Text style={{ fontSize: "12px" }}>
            {task.taskName.length > 30
              ? `${task.taskName.substring(0, 30)}...`
              : task.taskName}
          </Text>
        </Tooltip>
        {isSelected && selectedTasks.size > 1 && (
          <Tag
            color="blue"
            style={{ fontSize: "10px", margin: 0, padding: "0 4px" }}
          >
            +{selectedTasks.size}
          </Tag>
        )}
      </div>
    );
  }
);
DraggableTask.displayName = "DraggableTask";

/**
 * Deliberately a fixed height so the week columns can window their cards — the
 * plot tag and stage name are kept on one line for the same reason.
 */
const AllocatedTaskCard: React.FC<{
  task: ITaskAllocation;
  stageColor: string;
  onRemove: () => void;
}> = ({ task, stageColor, onRemove }) => {
  const [{ isDragging }, drag] = useDrag({
    type: "TASK",
    item: (): IDragItem => ({
      allocationKey: task.allocationKey,
      taskId: task.taskId,
      taskName: task.taskName,
      stageId: task.stageId,
      stageName: task.stageName,
      plotNumber: task.plotNumber,
      monthWeek: task.monthWeek,
      status: task.status
    }),
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging()
    })
  });

  return (
    <div
      ref={drag}
      style={{
        boxSizing: "border-box",
        height: ALLOCATED_CARD_HEIGHT - 6,
        padding: "6px 8px",
        background: isDragging ? "#e6f7ff" : "#ffffff",
        border: "1px solid #d9d9d9",
        borderLeft: `3px solid ${stageColor}`,
        borderRadius: "4px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        cursor: "move",
        opacity: isDragging ? 0.5 : 1
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "4px"
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Tooltip
            title={`${task.taskName} (${task.stageName})`}
            placement="topLeft"
          >
            <Text
              style={{
                fontSize: "12px",
                fontWeight: 500,
                display: "block",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {task.taskName}
            </Text>
          </Tooltip>
          <div
            style={{
              display: "flex",
              gap: "4px",
              marginTop: "2px",
              flexWrap: "nowrap",
              alignItems: "center",
              minWidth: 0
            }}
          >
            <Tag
              color="geekblue"
              style={{ fontSize: "10px", margin: 0, flexShrink: 0 }}
            >
              P{task.plotNumber}
            </Tag>
            <Text
              type="secondary"
              style={{
                fontSize: "10px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {task.stageName}
            </Text>
          </div>
        </div>
        <Button
          type="text"
          size="small"
          danger
          onClick={onRemove}
          style={{ flexShrink: 0, padding: "0 4px", height: "20px" }}
        >
          ×
        </Button>
      </div>
    </div>
  );
};

const WeekDropZone = React.memo(
  ({
    monthWeek,
    weekNumber,
    allocatedTasks,
    setAllocationsByWeek,
    setSelectedTasks
  }: {
    monthWeek: string;
    weekNumber: number;
    allocatedTasks: ITaskAllocation[];
    setAllocationsByWeek: React.Dispatch<
      React.SetStateAction<WeeklyAllocations>
    >;
    setSelectedTasks: React.Dispatch<React.SetStateAction<Set<string>>>;
  }) => {
    const today = new Date();
    const currentMonthWeek = `${today.getFullYear()}-${String(
      today.getMonth() + 1
    ).padStart(2, "0")}-W${Math.ceil(today.getDate() / 7)}`;

    const isCurrentWeek = monthWeek === currentMonthWeek;
    const [listRef, listHeight] = useElementHeight();

    const [{ isOver }, drop] = useDrop({
      accept: "TASK",
      drop: (item: IDragItem) => {
        // Only a card dragged out of a week carries `monthWeek`, so dropping one
        // back where it came from is the only no-op: anything from the
        // unallocated panel is by definition not allocated yet, and a
        // multi-drag can only start there.
        if (item.monthWeek === monthWeek) return;

        const draggedTasks =
          item.isMultiple && item.multipleTasksData
            ? item.multipleTasksData
            : [item];
        const sourceWeek = item.monthWeek;

        setAllocationsByWeek((prev) => {
          const afterRemoval = sourceWeek
            ? withWeekTasks(
                prev,
                sourceWeek,
                (prev.get(sourceWeek) ?? []).filter(
                  (task) => task.allocationKey !== item.allocationKey
                )
              )
            : prev;

          return withWeekTasks(afterRemoval, monthWeek, [
            ...(prev.get(monthWeek) ?? []),
            ...draggedTasks.map((task) => ({
              allocationKey: task.allocationKey,
              taskId: task.taskId,
              taskName: task.taskName,
              stageId: task.stageId,
              stageName: task.stageName,
              monthWeek,
              plotNumber: task.plotNumber,
              status: task.status
            }))
          ]);
        });
        setSelectedTasks(new Set());
        message.success(
          draggedTasks.length === 1
            ? `Task allocated to Week ${weekNumber}`
            : `${draggedTasks.length} tasks allocated to Week ${weekNumber}`
        );
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver()
      })
    });

    const handleRemove = React.useCallback(
      (allocationKey: string) => {
        setAllocationsByWeek((prev) =>
          withWeekTasks(
            prev,
            monthWeek,
            (prev.get(monthWeek) ?? []).filter(
              (task) => task.allocationKey !== allocationKey
            )
          )
        );
      },
      [monthWeek, setAllocationsByWeek]
    );

    return (
      <div
        ref={drop}
        style={{
          background: isOver
            ? "#e6f7ff"
            : isCurrentWeek
              ? "#e6f7ff"
              : "#fafafa",
          border: isOver
            ? "2px dashed #1890ff"
            : isCurrentWeek
              ? "1px solid #91d5ff"
              : "1px solid #e8e8e8",
          borderRadius: "8px",
          padding: "12px",
          height: "500px",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.2s"
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
            borderBottom:
              allocatedTasks.length > 0 ? "1px solid #f0f0f0" : "none",
            paddingBottom: allocatedTasks.length > 0 ? "8px" : "0",
            flexShrink: 0
          }}
        >
          <Text
            strong
            style={{
              fontSize: "14px",
              color: isCurrentWeek ? "#1890ff" : undefined
            }}
          >
            Week {weekNumber}
          </Text>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            {isCurrentWeek && (
              <Tag color="blue" style={{ fontSize: "10px", margin: 0 }}>
                Current
              </Tag>
            )}
            {allocatedTasks.length > 0 && (
              <Tag color="blue" style={{ fontSize: "11px", margin: 0 }}>
                {allocatedTasks.length} task
                {allocatedTasks.length !== 1 ? "s" : ""}
              </Tag>
            )}
          </div>
        </div>

        <div ref={listRef} style={{ flex: 1, minHeight: 0 }}>
          {allocatedTasks.length === 0 ? (
            <div
              style={{
                color: "#999",
                textAlign: "center",
                padding: "20px",
                fontSize: "12px",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              Drop tasks here
            </div>
          ) : (
            listHeight > 0 && (
              <FixedSizeList
                height={listHeight}
                width="100%"
                itemCount={allocatedTasks.length}
                itemSize={ALLOCATED_CARD_HEIGHT}
              >
                {({ index, style }) => {
                  const task = allocatedTasks[index];
                  return (
                    <div style={style}>
                      <AllocatedTaskCard
                        task={task}
                        stageColor={getStageColor(task.stageId)}
                        onRemove={() => handleRemove(task.allocationKey)}
                      />
                    </div>
                  );
                }}
              </FixedSizeList>
            )
          )}
        </div>
      </div>
    );
  }
);
WeekDropZone.displayName = "WeekDropZone";

/**
 * Reports its natural height so `VariableSizeList` can lay out group rows whose
 * height depends on how many task chips wrap. A row is drawn at its cached (or
 * estimated) height first, then corrected once measured.
 */
const MeasuredGroupRow = ({
  index,
  style,
  onMeasure,
  children
}: {
  index: number;
  style: React.CSSProperties;
  onMeasure: (index: number, height: number) => void;
  children: React.ReactNode;
}) => {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const report = () => onMeasure(index, node.offsetHeight);
    report();

    const observer = new ResizeObserver(report);
    observer.observe(node);
    return () => observer.disconnect();
  }, [index, onMeasure]);

  // `height` is dropped from react-window's style so the row can size to its
  // content; the measurement then feeds the offset of every row below it.
  return (
    <div style={{ ...style, height: undefined }}>
      <div ref={ref}>{children}</div>
    </div>
  );
};

const UnallocatedGroupRow = React.memo(
  ({
    group,
    carryForwardTaskKeys,
    allTasks,
    selectedTasks,
    setSelectedTasks
  }: {
    group: IUnallocatedGroup;
    carryForwardTaskKeys: Set<string>;
    allTasks: IAvailableTask[];
    selectedTasks: Set<string>;
    setSelectedTasks: React.Dispatch<React.SetStateAction<Set<string>>>;
  }) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: GROUP_COLUMNS,
        gap: 8,
        padding: "8px 12px",
        borderBottom: "1px solid #f0f0f0",
        alignItems: "start",
        background: "#ffffff"
      }}
    >
      <div>
        <Tag color="blue" style={{ fontWeight: "bold", margin: 0 }}>
          Plot {group.plotNumber}
        </Tag>
      </div>
      <div style={{ minWidth: 0 }}>
        <Tag
          color={group.stageColor || "#ccc"}
          style={{ fontWeight: 500, margin: 0 }}
        >
          {group.stageName}
        </Tag>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, minWidth: 0 }}>
        {group.tasks.map((task) => (
          <DraggableTask
            key={task.allocationKey}
            task={task}
            isCarryForward={carryForwardTaskKeys.has(task.allocationKey)}
            allTasks={allTasks}
            selectedTasks={selectedTasks}
            setSelectedTasks={setSelectedTasks}
          />
        ))}
      </div>
    </div>
  )
);
UnallocatedGroupRow.displayName = "UnallocatedGroupRow";

const UnallocatedPanel = React.memo(
  ({
    carryForwardTaskKeys,
    unallocatedTasks,
    selectedTasks,
    setSelectedTasks
  }: {
    carryForwardTaskKeys: Set<string>;
    unallocatedTasks: IAvailableTask[];
    selectedTasks: Set<string>;
    setSelectedTasks: React.Dispatch<React.SetStateAction<Set<string>>>;
  }) => {
    const groups = React.useMemo(() => {
      const tasksByPlotAndStage = new Map<string, IUnallocatedGroup>();

      // Group unallocated tasks by plot and stage
      unallocatedTasks.forEach((task) => {
        const key = `${task.plotNumber}-${task.stageId}`;
        const group = tasksByPlotAndStage.get(key);
        if (group) {
          group.tasks.push(task);
        } else {
          tasksByPlotAndStage.set(key, {
            key,
            plotNumber: task.plotNumber,
            stageId: task.stageId,
            stageName: task.stageName,
            stageColor: task.stageColor,
            tasks: [task]
          });
        }
      });

      // Sort by plot then stage
      return [...tasksByPlotAndStage.values()].sort((a, b) => {
        if (a.plotNumber !== b.plotNumber) {
          return Number(a.plotNumber) - Number(b.plotNumber);
        }
        return a.stageName.localeCompare(b.stageName);
      });
    }, [unallocatedTasks]);

    const listRef = React.useRef<VariableSizeList>(null);
    // Heights are cached against a group's identity and task count rather than
    // its row index, so allocating a task away only invalidates its own group.
    const heightsRef = React.useRef(new Map<string, number>());
    const sizeKey = (group: IUnallocatedGroup) =>
      `${group.key}|${group.tasks.length}`;

    // Group contents just changed, so the row offsets react-window cached from
    // the previous render are stale.
    React.useEffect(() => {
      listRef.current?.resetAfterIndex(0);
    }, [groups]);

    const handleMeasure = React.useCallback(
      (index: number, height: number) => {
        const key = sizeKey(groups[index]);
        if (heightsRef.current.get(key) === height) return;
        heightsRef.current.set(key, height);
        listRef.current?.resetAfterIndex(index);
      },
      [groups]
    );

    return (
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #f0f0f0",
          borderRadius: 4,
          overflow: "hidden"
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: GROUP_COLUMNS,
            gap: 8,
            padding: "8px 12px",
            background: "#fafafa",
            borderBottom: "1px solid #f0f0f0",
            fontSize: 12,
            fontWeight: 500
          }}
        >
          <span>Plot</span>
          <span>Stage</span>
          <span>Tasks</span>
        </div>

        <VariableSizeList
          ref={listRef}
          height={UNALLOCATED_LIST_HEIGHT}
          width="100%"
          itemCount={groups.length}
          itemSize={(index) =>
            heightsRef.current.get(sizeKey(groups[index])) ??
            ESTIMATED_GROUP_HEIGHT
          }
          estimatedItemSize={ESTIMATED_GROUP_HEIGHT}
        >
          {({ index, style }) => (
            <MeasuredGroupRow
              index={index}
              style={style}
              onMeasure={handleMeasure}
            >
              <UnallocatedGroupRow
                group={groups[index]}
                carryForwardTaskKeys={carryForwardTaskKeys}
                allTasks={unallocatedTasks}
                selectedTasks={selectedTasks}
                setSelectedTasks={setSelectedTasks}
              />
            </MeasuredGroupRow>
          )}
        </VariableSizeList>
      </div>
    );
  }
);
UnallocatedPanel.displayName = "UnallocatedPanel";
